import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import { useToast } from '../../context/ToastContext';

const ZeerowBillingConfig = () => {
  const toast = useToast();
  const navigate = useNavigate();

  const [config, setConfig] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [kycMakerRate, setKycMakerRate] = useState('');
  const [upiChargeRate, setUpiChargeRate] = useState('');
  const [txnPercentage, setTxnPercentage] = useState('');
  const [changeReason, setChangeReason] = useState('');

  useEffect(() => {
    fetchConfig();
    fetchHistory();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/v1/admin/zeerow/billing-config');
      setConfig(response.data);
      setError('');
    } catch (err) {
      if (err.response?.status === 404) {
        setConfig(null);
        setError('');
      } else {
        const msg = err.response?.data?.error || 'Failed to load billing configuration';
        setError(msg);
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await api.get('/api/v1/admin/zeerow/billing-config/history');
      setHistory(response.data);
    } catch (err) {
      setHistory([]);
    }
  };

  const handleEdit = () => {
    setKycMakerRate(config?.kycMakerRate?.toString() || '0');
    setUpiChargeRate(config?.upiChargeRate?.toString() || '0');
    setTxnPercentage(config?.txnPercentage?.toString() || '0');
    setChangeReason('');
    setIsEditing(true);
    setError('');
  };

  const handleCancel = () => {
    setIsEditing(false);
    setChangeReason('');
    setError('');
  };

  const handleSave = async () => {
    const kycVal = parseFloat(kycMakerRate);
    const upiVal = parseFloat(upiChargeRate);
    const txnVal = parseFloat(txnPercentage);

    if (isNaN(kycVal) || kycVal < 0) {
      setError('KYC Maker Rate must be 0 or greater');
      toast.error('KYC Maker Rate must be 0 or greater');
      return;
    }
    if (isNaN(upiVal) || upiVal < 0) {
      setError('UPI Charge Rate must be 0 or greater');
      toast.error('UPI Charge Rate must be 0 or greater');
      return;
    }
    if (isNaN(txnVal) || txnVal < 0 || txnVal > 100) {
      setError('Transaction Percentage must be between 0 and 100');
      toast.error('Transaction Percentage must be between 0 and 100');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const response = await api.post('/api/v1/admin/zeerow/billing-config', {
        kycMakerRate: kycVal,
        upiChargeRate: upiVal,
        txnPercentage: txnVal,
        changeReason: changeReason || null,
      });
      setConfig(response.data);
      setIsEditing(false);
      setChangeReason('');
      toast.success('Billing configuration updated successfully');
      await fetchHistory();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to save billing configuration';
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const totalPages = Math.ceil(history.length / itemsPerPage);
  const paginatedHistory = history.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500 text-sm">Loading billing configuration...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full px-6 py-8 space-y-6">

        {/* Top bar */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Zeerow Billing Configuration</h1>
            <p className="text-sm text-gray-500 mt-1">Global — applies to all Zeerow enterprises</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin/zeerow/invoices')}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors"
            >
              View Zeerow Invoices
            </button>
            {!isEditing && config && (
              <button
                onClick={handleEdit}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                Edit Config
              </button>
            )}
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
            <p className="text-sm text-red-600">{error}</p>
            <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 text-lg leading-none">&times;</button>
          </div>
        )}

        {/* Config card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Rate Configuration</h2>
              <p className="text-sm text-gray-400 mt-0.5">Rates used to compute Zeerow combined invoices</p>
            </div>
            {isEditing && (
              <span className="px-2 py-1 bg-blue-50 text-blue-600 text-xs font-medium rounded-md">Editing</span>
            )}
          </div>

          {config ? (
            <>
              <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-3">
                {/* KYC Maker Rate */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    KYC Maker Rate
                  </label>
                  {!isEditing ? (
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700 text-sm">
                      ₹{parseFloat(config.kycMakerRate || 0).toFixed(2)} per KYC maker
                    </div>
                  ) : (
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={kycMakerRate}
                        onChange={(e) => setKycMakerRate(e.target.value)}
                        className="w-full border border-gray-200 rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                        placeholder="0.00"
                      />
                    </div>
                  )}
                </div>

                {/* UPI Charge Rate */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    UPI Charge Rate
                  </label>
                  {!isEditing ? (
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700 text-sm">
                      ₹{parseFloat(config.upiChargeRate || 0).toFixed(2)} per UPI transaction
                    </div>
                  ) : (
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={upiChargeRate}
                        onChange={(e) => setUpiChargeRate(e.target.value)}
                        className="w-full border border-gray-200 rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                        placeholder="0.00"
                      />
                    </div>
                  )}
                </div>

                {/* Transaction Percentage */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Transaction Percentage
                  </label>
                  {!isEditing ? (
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700 text-sm">
                      {parseFloat(config.txnPercentage || 0).toFixed(2)}% of total transaction amount
                    </div>
                  ) : (
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={txnPercentage}
                        onChange={(e) => setTxnPercentage(e.target.value)}
                        className="w-full border border-gray-200 rounded-lg pl-3 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                        placeholder="0.00"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Change Reason (edit mode only) */}
              {isEditing && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Change Reason <span className="text-gray-400 font-normal">(Optional)</span>
                  </label>
                  <textarea
                    value={changeReason}
                    onChange={(e) => setChangeReason(e.target.value)}
                    placeholder="Why are you updating these rates?"
                    rows={2}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none"
                  />
                </div>
              )}

              {/* Last updated info */}
              {!isEditing && (
                <div className="text-xs text-gray-400 mb-6">
                  <span>Last updated by: </span>
                  <span className="font-medium text-gray-600">{config.updatedByName || '—'}</span>
                  {config.updatedAt && (
                    <span> &nbsp;|&nbsp; Updated at: <span className="font-medium text-gray-600">{formatDate(config.updatedAt)}</span></span>
                  )}
                </div>
              )}

              {/* Action buttons */}
              {isEditing && (
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={saving}
                    className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-10">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <p className="text-gray-700 font-medium mb-1">No configuration found</p>
              <p className="text-sm text-gray-400">Zeerow billing rates have not been set up yet. Please contact your system administrator to configure the rates.</p>
            </div>
          )}
        </div>

        {/* History table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <p className="text-sm font-semibold text-gray-700">Edit History</p>
            <p className="text-xs text-gray-400 mt-0.5">
              {history.length > 0 ? `${history.length} change${history.length === 1 ? '' : 's'} recorded` : 'No changes recorded yet'}
            </p>
          </div>

          {history.length === 0 ? (
            <div className="py-10 text-center">
              <p className="text-sm text-gray-400">No edit history yet.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-gray-400 uppercase bg-gray-50">
                    <tr>
                      <th className="text-left px-5 py-3 font-medium whitespace-nowrap">Date &amp; Time</th>
                      <th className="text-left px-5 py-3 font-medium whitespace-nowrap">Changed By</th>
                      <th className="text-left px-5 py-3 font-medium whitespace-nowrap">KYC Maker Rate</th>
                      <th className="text-left px-5 py-3 font-medium whitespace-nowrap">UPI Charge Rate</th>
                      <th className="text-left px-5 py-3 font-medium whitespace-nowrap">Txn %</th>
                      <th className="text-left px-5 py-3 font-medium whitespace-nowrap">Reason</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {paginatedHistory.map((entry) => (
                      <tr key={entry.id} className="hover:bg-gray-50">
                        <td className="px-5 py-3 text-gray-700 whitespace-nowrap">{formatDate(entry.createdAt)}</td>
                        <td className="px-5 py-3 text-gray-700">{entry.changedByName || '—'}</td>
                        <td className="px-5 py-3 text-gray-700">₹{parseFloat(entry.kycMakerRate || 0).toFixed(2)}</td>
                        <td className="px-5 py-3 text-gray-700">₹{parseFloat(entry.upiChargeRate || 0).toFixed(2)}</td>
                        <td className="px-5 py-3 text-gray-700">{parseFloat(entry.txnPercentage || 0).toFixed(2)}%</td>
                        <td className="px-5 py-3 text-gray-500 text-xs">{entry.changeReason || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
                  <p className="text-xs text-gray-400">Page {currentPage} of {totalPages}</p>
                  <div className="flex items-center gap-1">
                    <button
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      className="px-2 py-1 text-xs border border-gray-200 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const p = Math.max(1, Math.min(currentPage - 2, totalPages - 4)) + i;
                      return (
                        <button
                          key={p}
                          onClick={() => setCurrentPage(p)}
                          className={`px-2 py-1 text-xs rounded-lg transition-colors ${
                            p === currentPage
                              ? 'bg-blue-600 text-white'
                              : 'border border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          {p}
                        </button>
                      );
                    })}
                    <button
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      className="px-2 py-1 text-xs border border-gray-200 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

      </div>
    </div>
  );
};

export default ZeerowBillingConfig;
